import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Tooltip, Typography} from '@mui/material';
import {useDispatch} from 'react-redux';
import {resetPaginationState} from '../../../hoc/pagination/paginationSlice';
import withPagination from '../../../hoc/pagination/withPagination';
import AdmicityTable from '../../../shared-components/AdmicityTable';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import Uploader from '../../SchoolManagement/Setup/Uploader';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import UploadFile from '@mui/icons-material/UploadFile';
import { useConfirm } from 'material-ui-confirm';

import {
    downloadSchoolDocument,
    useDeleteSchoolDocumentMutation,
    useGetSchoolDocumentsQuery,
    useUpdateSchoolDocumentAudienceMutation,
    useUploadSchoolDocumentsMutation
} from '../../../api/services/filesService';
import useFileDownloader from '../../../utility/hooks/useFileDownloader';
import useNotification from '../../../utility/hooks/useNotification';
import {withIndefiniteArticle} from '../../../utility/textUtil';
import DocumentAudienceGroupFields from './DocumentAudienceGroupFields';
import DocumentAudienceModeFields from './DocumentAudienceModeFields';
import DocumentAudienceEmployeeFields from './DocumentAudienceEmployeeFields';
import {useGetGroupFiltersQuery} from '../../../api/services/groupService';
import {GROUP_MEMBERS_TYPE} from '../../../constants/GroupMembersType';
import {
    buildUpdateSchoolDocumentAudienceBody,
    formatSchoolDocumentAudienceSummary,
    schoolDocumentToAudienceEditorState,
} from './documentAudienceUtils';
import {
    DEFAULT_SCHOOL_DOCUMENT_ASSIGNMENT_TYPE,
    DEFAULT_SCHOOL_DOCUMENT_AUDIENCE_MODE,
    DEFAULT_SCHOOL_DOCUMENT_NOTIFICATION_MODE,
    formatSchoolDocumentAssignmentType,
    formatSchoolDocumentAudienceMode,
    formatSchoolDocumentNotificationMode,
    SCHOOL_DOCUMENT_ASSIGNMENT_TYPE,
} from './schoolDocumentModes';

const emptyAudienceSettings = () => ({
    groupFilters: [],
    studentFilters: [],
    applicationFilters: [],
    enquiryFilters: [],
    employeeFilters: []
});

const DocumentsTab = () => {
    const dispatch = useDispatch();
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const [openAddDocumentsDialog, setOpenAddDocumentsDialog] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState(new Map());
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadSizeExceedsLimit, setIsUploadSizeExceedsLimit] = useState(false);
    const [strictRecipientsFiltering, setStrictRecipientsFiltering] = useState(false);
    const [audienceSettings, setAudienceSettings] = useState(emptyAudienceSettings);
    const [uploadAssignmentType, setUploadAssignmentType] = useState(DEFAULT_SCHOOL_DOCUMENT_ASSIGNMENT_TYPE);
    const [uploadAudienceMode, setUploadAudienceMode] = useState(DEFAULT_SCHOOL_DOCUMENT_AUDIENCE_MODE);
    const [uploadNotificationMode, setUploadNotificationMode] = useState(DEFAULT_SCHOOL_DOCUMENT_NOTIFICATION_MODE);

    const [audienceEditDocument, setAudienceEditDocument] = useState(null);
    const [editStrictRecipientsFiltering, setEditStrictRecipientsFiltering] = useState(false);
    const [editAudienceSettings, setEditAudienceSettings] = useState(emptyAudienceSettings);
    const [editAudienceMode, setEditAudienceMode] = useState(DEFAULT_SCHOOL_DOCUMENT_AUDIENCE_MODE);
    const [editNotificationMode, setEditNotificationMode] = useState(DEFAULT_SCHOOL_DOCUMENT_NOTIFICATION_MODE);

    const {data: groupFiltersResponse = {modules: []}} = useGetGroupFiltersQuery(undefined);
    const [uploadFiles] = useUploadSchoolDocumentsMutation();
    const [deleteFile] = useDeleteSchoolDocumentMutation();
    const [updateDocumentAudience, {isLoading: isSavingAudience}] =
        useUpdateSchoolDocumentAudienceMutation();
    const confirm = useConfirm();

    const Table = withPagination(AdmicityTable, useGetSchoolDocumentsQuery);

    const downloadFile = useFileDownloader();

    const isUploadForEmployees = uploadAssignmentType === SCHOOL_DOCUMENT_ASSIGNMENT_TYPE.employees;
    const editAssignmentType = audienceEditDocument?.assignmentType ?? SCHOOL_DOCUMENT_ASSIGNMENT_TYPE.students;
    const isEditForEmployees = Number(editAssignmentType) === SCHOOL_DOCUMENT_ASSIGNMENT_TYPE.employees;

    const studentProfileGroupModules = useMemo(() => {
        const modules = groupFiltersResponse.modules || [];
        return modules
            .filter(m => m.membersType !== GROUP_MEMBERS_TYPE.employee)
            .map(({filters, ...rest}) => ({
                filters: filters.filter(f => f.membersType !== GROUP_MEMBERS_TYPE.employee),
                ...rest
            }));
    }, [groupFiltersResponse.modules]);

    const employeeProfileGroupModules = useMemo(() => {
        const modules = groupFiltersResponse.modules || [];
        return modules
            .filter(m =>
                m.membersType === GROUP_MEMBERS_TYPE.employee ||
                m.filters?.some(f => f.membersType === GROUP_MEMBERS_TYPE.employee)
            )
            .map(({filters, ...rest}) => ({
                filters: (filters || []).filter(f => f.membersType === GROUP_MEMBERS_TYPE.employee),
                ...rest
            }))
            .filter(m => (m.filters || []).length > 0 || m.membersType === GROUP_MEMBERS_TYPE.employee);
    }, [groupFiltersResponse.modules]);

    const uploadGroupModules = isUploadForEmployees ? employeeProfileGroupModules : studentProfileGroupModules;
    const editGroupModules = isEditForEmployees ? employeeProfileGroupModules : studentProfileGroupModules;

    const tableColumns = useMemo(() => ([
        {
            field: 'name',
            headerName: 'Name',
            editable: false,
            sortable: false,
            minWidth: 200,
            flex: 1
        },
        {
            field: 'assignmentType',
            headerName: 'Profiles',
            editable: false,
            sortable: false,
            minWidth: 110,
            renderCell: (params) => (
                <Typography variant="body2" sx={{alignSelf: 'center'}}>
                    {formatSchoolDocumentAssignmentType(params.row.assignmentType)}
                </Typography>
            )
        },
        {
            field: 'audienceSummary',
            headerName: 'Who can see (groups)',
            editable: false,
            sortable: false,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                const summary = formatSchoolDocumentAudienceSummary(params.row);
                return (
                    <Tooltip title={summary} placement="top-start">
                        <Typography
                            variant="body2"
                            sx={{
                                alignSelf: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                            }}
                        >
                            {summary}
                        </Typography>
                    </Tooltip>
                );
            }
        },
        {
            field: 'audienceMode',
            headerName: 'Audience',
            editable: false,
            sortable: false,
            minWidth: 110,
            renderCell: (params) => (
                <Typography variant="body2" sx={{alignSelf: 'center'}}>
                    {formatSchoolDocumentAudienceMode(params.row.audienceMode)}
                </Typography>
            )
        },
        {
            field: 'notificationMode',
            headerName: 'Notifications',
            editable: false,
            sortable: false,
            minWidth: 130,
            renderCell: (params) => (
                <Typography variant="body2" sx={{alignSelf: 'center'}}>
                    {formatSchoolDocumentNotificationMode(params.row.notificationMode)}
                </Typography>
            )
        },
    ]), []);

    useEffect(() => {
        return () => {
            dispatch(resetPaginationState());
        };
    }, []);

    useEffect(() => {
        if (audienceSettings.groupFilters.length < 2) {
            setStrictRecipientsFiltering(false);
        }
    }, [audienceSettings.groupFilters.length]);

    useEffect(() => {
        if (editAudienceSettings.groupFilters.length < 2) {
            setEditStrictRecipientsFiltering(false);
        }
    }, [editAudienceSettings.groupFilters.length]);

    const handleDialogClose = () => {
        setOpenAddDocumentsDialog(false);
        setSelectedFiles(new Map());
        setIsUploading(false);
        setIsUploadSizeExceedsLimit(false);
        setStrictRecipientsFiltering(false);
        setAudienceSettings(emptyAudienceSettings());
        setUploadAssignmentType(DEFAULT_SCHOOL_DOCUMENT_ASSIGNMENT_TYPE);
        setUploadAudienceMode(DEFAULT_SCHOOL_DOCUMENT_AUDIENCE_MODE);
        setUploadNotificationMode(DEFAULT_SCHOOL_DOCUMENT_NOTIFICATION_MODE);
    };

    const handleCloseAudienceEdit = () => {
        setAudienceEditDocument(null);
        setEditStrictRecipientsFiltering(false);
        setEditAudienceSettings(emptyAudienceSettings());
        setEditAudienceMode(DEFAULT_SCHOOL_DOCUMENT_AUDIENCE_MODE);
        setEditNotificationMode(DEFAULT_SCHOOL_DOCUMENT_NOTIFICATION_MODE);
    };

    const handleOpenAudienceEdit = (doc) => {
        const {
            strictRecipientsFiltering: strict,
            audienceSettings: next,
            audienceMode,
            notificationMode,
        } = schoolDocumentToAudienceEditorState(doc);
        setEditStrictRecipientsFiltering(strict);
        setEditAudienceSettings(next);
        setEditAudienceMode(audienceMode);
        setEditNotificationMode(notificationMode);
        setAudienceEditDocument(doc);
    };

    const handleSaveAudienceEdit = async () => {
        if (!audienceEditDocument) return;
        try {
            const body = buildUpdateSchoolDocumentAudienceBody(
                editAudienceSettings.groupFilters,
                editStrictRecipientsFiltering,
                {
                    audienceMode: editAudienceMode,
                    notificationMode: editNotificationMode,
                    employeeFilters: isEditForEmployees ? editAudienceSettings.employeeFilters : [],
                }
            );
            await updateDocumentAudience({
                schoolDocumentId: audienceEditDocument.id,
                groupFilters: body.groupFilters,
                employeeIds: body.employeeIds,
                strictRecipientsFiltering: body.strictRecipientsFiltering,
                audienceMode: body.audienceMode,
                notificationMode: body.notificationMode,
            }).unwrap();
            showSuccessNotification('Audience updated');
            handleCloseAudienceEdit();
        } catch {
            showErrorNotification('Could not update audience. Check the document and try again.');
        }
    };

    const handleAssignmentTypeChange = (nextType) => {
        setUploadAssignmentType(nextType);
        setAudienceSettings(emptyAudienceSettings());
        setStrictRecipientsFiltering(false);
    };

    const handleFilesUpload = async () => {
        setIsUploading(true);
        const groupFilters = audienceSettings.groupFilters;
        const payload = {
            files: selectedFiles,
            assignmentType: uploadAssignmentType,
        };
        if (groupFilters.length > 0) {
            payload.groupFiltersJson = JSON.stringify(
                groupFilters.map(f => ({filterId: f.id, excluded: !!f.excluded}))
            );
            if (groupFilters.length > 1) {
                payload.strictRecipientsFiltering = strictRecipientsFiltering;
            }
        }
        if (isUploadForEmployees && audienceSettings.employeeFilters.length > 0) {
            payload.employeeIdsJson = JSON.stringify(
                audienceSettings.employeeFilters.map(employee => employee.id).filter(id => id != null)
            );
        }
        payload.audienceMode = uploadAudienceMode;
        payload.notificationMode = uploadNotificationMode;
        await uploadFiles(payload);
        handleDialogClose();
    };

    const handleFilesSelect = (files) => {
        if (!files?.length) return;
        const file = files[0];
        setSelectedFiles(new Map([[file.name, file]]));
    };

    const handleFileDeleteFromUploadContext = (filename) => {
        const files = new Map([...selectedFiles]);

        files.delete(filename);
        setSelectedFiles(files);
    };

    const handleDeleteFile = async (file) => {
        try {
            await confirm({
                title: 'Confirm Deletion',
                description: (
                    <>
                        Are you sure you want to delete this document?
                        <br /><br />
                        <strong>{file.name}</strong>
                    </>
                ),
                confirmationText: 'Delete',
                cancellationText: 'Cancel',
            });
            await deleteFile({documentId: file.id});
        } catch {

        }
    };

    const handleDownloadFile = async (file) => await downloadFile(
        downloadSchoolDocument,
        {documentId: file.id}
    );

    const profileLabel = isUploadForEmployees ? 'employee' : 'student';
    const profileLabelPlural = isUploadForEmployees ? 'employees' : 'students';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minHeight: '100%',
                paddingTop: 2,
            }}
        >
            <div>
                <Button
                    sx={{
                        mb: 2,
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    startIcon={<UploadFile/>}
                    onClick={() => setOpenAddDocumentsDialog(true)}
                >
                    Add document
                </Button>
            </div>
            <Table
                columns={tableColumns}
                rowActions={[
                    {
                        label: 'Edit audience',
                        action: (row) => handleOpenAudienceEdit(row)
                    },
                    {
                        label: 'Download',
                        action: handleDownloadFile
                    },
                    {
                        label: 'Delete',
                        action: handleDeleteFile
                    },
                ]}
                noRowsOverlay={{
                    icon: <InsertDriveFileRoundedIcon/>,
                    text: 'No documents have been added.'
                }}
            />
            <AdmicityDialog
                handleClose={handleDialogClose}
                title={'Add document'}
                open={openAddDocumentsDialog}
                actions={[
                    {label: 'Cancel', onClick: handleDialogClose},
                    {
                        label: 'Upload',
                        onClick: handleFilesUpload,
                        disabled: (isUploading || selectedFiles.size === 0) || isUploadSizeExceedsLimit,
                        startIcon: isUploading ? <CircularProgress color="inherit" size={16}/> : undefined
                    }
                ]}
                maxWidth={'md'}
            >
                <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                        Choose one file. It appears on each {profileLabel} profile under School documents when it matches the
                        rules below. Leave groups empty to make the upload visible on every {profileLabel} profile.
                    </Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel id="school-doc-assignment-type-label">Show on</InputLabel>
                        <Select
                            labelId="school-doc-assignment-type-label"
                            label="Show on"
                            value={uploadAssignmentType}
                            onChange={(e) => handleAssignmentTypeChange(Number(e.target.value))}
                        >
                            <MenuItem value={SCHOOL_DOCUMENT_ASSIGNMENT_TYPE.students}>Student profiles</MenuItem>
                            <MenuItem value={SCHOOL_DOCUMENT_ASSIGNMENT_TYPE.employees}>Employee profiles</MenuItem>
                        </Select>
                    </FormControl>
                    <DocumentAudienceGroupFields
                        data={uploadGroupModules}
                        settings={audienceSettings}
                        setSettings={setAudienceSettings}
                        strictRecipientsFiltering={strictRecipientsFiltering}
                        setStrictRecipientsFiltering={setStrictRecipientsFiltering}
                        disableStrictRecipientsFiltering={audienceSettings.groupFilters.length < 2}
                        groupFiltersLabel={`Who can see these documents on ${withIndefiniteArticle(profileLabel)} profile`}
                    />
                    {isUploadForEmployees
                        ? (
                            <DocumentAudienceEmployeeFields
                                employeeFilters={audienceSettings.employeeFilters}
                                onEmployeeFiltersChange={(employeeFilters) =>
                                    setAudienceSettings(prev => ({...prev, employeeFilters}))
                                }
                            />
                        )
                        : null}
                    <DocumentAudienceModeFields
                        audienceMode={uploadAudienceMode}
                        notificationMode={uploadNotificationMode}
                        onAudienceModeChange={setUploadAudienceMode}
                        onNotificationModeChange={setUploadNotificationMode}
                        personLabel={profileLabelPlural}
                        notificationAudienceLabel={isUploadForEmployees ? 'Alerts to employees' : 'Alerts to families'}
                    />
                    <Uploader
                        singleFile
                        primaryLabel="Click to select one file or drag and drop"
                        totalUploadSizeInMb={25}
                        onFilesSelect={handleFilesSelect}
                        onFileDelete={handleFileDeleteFromUploadContext}
                        onUploadLimitExceed={setIsUploadSizeExceedsLimit}
                        value={Array.from(selectedFiles.values())}
                        readonly={isUploading}
                    />
                </Box>
            </AdmicityDialog>

            <AdmicityDialog
                handleClose={handleCloseAudienceEdit}
                title={audienceEditDocument ? `Edit audience — ${audienceEditDocument.name}` : 'Edit audience'}
                open={Boolean(audienceEditDocument)}
                actions={[
                    {label: 'Cancel', onClick: handleCloseAudienceEdit},
                    {
                        label: 'Save',
                        onClick: handleSaveAudienceEdit,
                        disabled: isSavingAudience,
                        startIcon: isSavingAudience
                            ? <CircularProgress color="inherit" size={16}/>
                            : undefined,
                    }
                ]}
                maxWidth={'md'}
            >
                {audienceEditDocument
                    ? (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                Changes apply to this file only. With no groups and no individual employees selected,
                                every {isEditForEmployees ? 'employee' : 'student'} profile can see it under School documents.
                            </Typography>
                            <DocumentAudienceGroupFields
                                key={audienceEditDocument.id}
                                data={editGroupModules}
                                settings={editAudienceSettings}
                                setSettings={setEditAudienceSettings}
                                strictRecipientsFiltering={editStrictRecipientsFiltering}
                                setStrictRecipientsFiltering={setEditStrictRecipientsFiltering}
                                disableStrictRecipientsFiltering={editAudienceSettings.groupFilters.length < 2}
                                groupFiltersLabel={`Who can see these documents on ${withIndefiniteArticle(isEditForEmployees ? 'employee' : 'student')} profile`}
                            />
                            {isEditForEmployees
                                ? (
                                    <DocumentAudienceEmployeeFields
                                        employeeFilters={editAudienceSettings.employeeFilters}
                                        onEmployeeFiltersChange={(employeeFilters) =>
                                            setEditAudienceSettings(prev => ({...prev, employeeFilters}))
                                        }
                                    />
                                )
                                : null}
                            <DocumentAudienceModeFields
                                audienceMode={editAudienceMode}
                                notificationMode={editNotificationMode}
                                onAudienceModeChange={setEditAudienceMode}
                                onNotificationModeChange={setEditNotificationMode}
                                personLabel={isEditForEmployees ? 'employees' : 'students'}
                                notificationAudienceLabel={isEditForEmployees ? 'Alerts to employees' : 'Alerts to families'}
                            />
                        </Box>
                    )
                    : null}
            </AdmicityDialog>
        </Box>
    );
};

export default DocumentsTab;
